import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import { MultiSelect } from 'primereact/multiselect'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'
import { Dropdown } from 'primereact/dropdown'
import { optionRow } from '@aytm/app/stats/page/question-views/components/controls/OptionLabel'

// styles
import style from '@aytm/app/stats/page/question-views/bpm/components/quadrant/styles/Controls.module.scss'

import {
  prepareOptionsChartLegends,
  prepareOptionsForControls,
  getPlaceholder,
  convertArrayToStrings
} from '@aytm/app/stats/page/question-views/bpm/lib/utils'

const labeledValue = label => option => (
  <>
    {label}: <b>{option?.text}</b>
  </>
)

const Controls = ({
  quadrantBrandsValue,
  quadrantXAxisValue,
  quadrantYAxisValue,
  quadrantRadiusValue,
  quadrantColorValue,
  quadrantChartSelectOptions,
  quadrantChartSelectOptionsWithNone,
  questionVariants,
  chartLegendMode,
  onModelChange
}) => {
  const entitiesOptions = prepareOptionsChartLegends(questionVariants, chartLegendMode)

  const selectOptions = prepareOptionsForControls(quadrantChartSelectOptions)
  const selectOptionsWithNone = prepareOptionsForControls(quadrantChartSelectOptionsWithNone)

  const onFilterChange = event => {
    const { name, value } = event.target
    onModelChange({
      [name]: value
    })
  }

  // the value comes off the event itself: the list's own Deselect all sends it without a field name
  const onEntitiesChange = event => onModelChange({ quadrantBrandsValue: event.value })

  return (
    <div className={style.row}>
      <MultiSelect
        data-testid="stat-bpm-quadrant-entities"
        name="quadrantBrandsValue"
        className={classNames(style.select, 'p-multiselect-rounded')}
        placeholder={`Filter by entities: ${getPlaceholder(quadrantBrandsValue, entitiesOptions)}`}
        fixedPlaceholder
        value={convertArrayToStrings(quadrantBrandsValue)}
        options={entitiesOptions}
        optionValue="value"
        optionLabel="text"
        itemTemplate={optionRow('bpm-quadrant-entities')}
        panelHeaderTemplate={MultiSelectHeader}
        panelFooterTemplate={MultiSelectFooter}
        removeIcon={null}
        onChange={onEntitiesChange}
      />
      <div className={style.column}>
        <Dropdown
          className={classNames(style.select, 'p-dropdown-rounded')}
          aria-label="X-axis"
          ariaLabel="X-axis"
          name="quadrantXAxisValue"
          value={quadrantXAxisValue}
          options={selectOptions}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          itemTemplate={optionRow('bpm-quadrant-x')}
          valueTemplate={labeledValue('X-axis')}
          onChange={onFilterChange}
        />
        <Dropdown
          className={classNames(style.select, 'p-dropdown-rounded')}
          aria-label="Y-axis"
          ariaLabel="Y-axis"
          name="quadrantYAxisValue"
          value={quadrantYAxisValue}
          options={selectOptions}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          itemTemplate={optionRow('bpm-quadrant-y')}
          valueTemplate={labeledValue('Y-axis')}
          onChange={onFilterChange}
        />
      </div>
      <div className={style.column}>
        <Dropdown
          className={classNames(style.select, 'p-dropdown-rounded')}
          aria-label="Size"
          ariaLabel="Size"
          name="quadrantRadiusValue"
          value={quadrantRadiusValue}
          options={selectOptionsWithNone}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          itemTemplate={optionRow('bpm-quadrant-size')}
          valueTemplate={labeledValue('Size')}
          onChange={onFilterChange}
        />
        <Dropdown
          className={classNames(style.select, 'p-dropdown-rounded')}
          aria-label="Color"
          ariaLabel="Color"
          name="quadrantColorValue"
          value={quadrantColorValue}
          options={selectOptionsWithNone}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          itemTemplate={optionRow('bpm-quadrant-color')}
          valueTemplate={labeledValue('Color')}
          onChange={onFilterChange}
        />
      </div>
    </div>
  )
}

Controls.displayName = 'BpmControlsQuadrant'

Controls.propTypes = {
  chartLegendMode: PropTypes.string,
  questionVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
  quadrantBrandsValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  quadrantXAxisValue: PropTypes.string,
  quadrantYAxisValue: PropTypes.string,
  quadrantColorValue: PropTypes.string,
  quadrantRadiusValue: PropTypes.string,
  quadrantChartSelectOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  quadrantChartSelectOptionsWithNone: PropTypes.arrayOf(PropTypes.object).isRequired,
  onModelChange: PropTypes.func.isRequired
}

export default Controls
